import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { DashboardPulse } from "@xop/dashboard-shared";

import { api } from "../../lib/api.js";
import { startOfAgentDayIso } from "../../lib/today.js";

/**
 * Single source of truth for the dashboard pulse query. Used by the
 * page and easily mocked in tests by stubbing `/api/dashboard/pulse`.
 *
 * Refetch cadence: 20s. We deliberately keep the previous data visible
 * during a refetch (the page header shows a small "Updating…" pip) so
 * the layout doesn't reflow every poll.
 *
 * We pass **UTC** midnight as `leads_since` so the headline's "N new leads
 * today" counts the SAME calendar day as the Sales ▸ Today tab AND the agent's
 * own UTC day — and, critically, the same day for EVERY viewer regardless of
 * their timezone (see `@/lib/today`; previously this was the browser's local
 * midnight, which made two colleagues in different timezones see different
 * counts of the same leads). `since` is part of the query key so the count
 * rolls over when the UTC day does.
 */
export function useDashboardPulse(): UseQueryResult<DashboardPulse, Error> {
  const since = startOfAgentDayIso();
  return useQuery({
    queryKey: ["dashboard", "pulse", since],
    queryFn: () =>
      api<DashboardPulse>(
        `/api/dashboard/pulse?leads_since=${encodeURIComponent(since)}`,
      ),
    refetchInterval: 20_000,
    refetchIntervalInBackground: false,
    placeholderData: (prev) => prev,
  });
}
